import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  
  const toggleQuestion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  
  const faqs = [
    {
      question: "Quanto tempo ci vuole in media per vendere un immobile?",
      answer: "Dipende da diversi fattori come la zona, il prezzo e le condizioni dell'immobile. La nostra media è di 45-60 giorni, significativamente inferiore alla media di mercato che si attesta intorno ai 120 giorni. Grazie al nostro metodo e alla nostra rete di acquirenti pre-qualificati, riusciamo a ridurre notevolmente i tempi di attesa."
    },
    {
      question: "Quali documenti sono necessari per vendere casa?",
      answer: "Per vendere casa sono necessari: documento d'identità e codice fiscale, atto di provenienza (atto notarile d'acquisto), visura e planimetria catastale, attestato di prestazione energetica (APE), documentazione urbanistica e documentazione condominiale se applicabile. Non preoccuparti, il nostro team ti guiderà passo dopo passo nella raccolta della documentazione necessaria."
    },
    {
      question: "Come viene stabilito il prezzo di vendita dell'immobile?",
      answer: "Il prezzo viene stabilito attraverso un'attenta analisi di mercato che considera diversi fattori: ubicazione, caratteristiche dell'immobile, condizioni, confronto con proprietà simili recentemente vendute nella stessa zona, andamento del mercato locale e attrattività della zona. La nostra valutazione è gratuita e senza impegno."
    },
    {
      question: "Devo essere presente durante le visite?",
      answer: "No, non è necessario. Ci occupiamo noi di tutte le visite, presentando l'immobile nel modo migliore ai potenziali acquirenti. Ti forniremo un feedback dettagliato dopo ogni visita. Questo ti permette di risparmiare tempo e di evitare l'imbarazzo o lo stress che alcuni proprietari provano durante le visite."
    },
    {
      question: "Cosa succede se non riuscite a vendere il mio immobile?",
      answer: "Nel caso improbabile in cui non riuscissimo a vendere il tuo immobile entro il periodo concordato, rivediamo insieme la strategia: analizziamo i feedback ricevuti, valutiamo eventuali aggiustamenti di prezzo o migliorie da apportare, e ridefiniamo il piano di marketing. Il nostro obiettivo è vendere il tuo immobile al miglior prezzo possibile."
    },
    {
      question: "Quali costi devo sostenere per vendere casa?",
      answer: "I costi principali includono la provvigione dell'agenzia (concordata in anticipo), le spese notarili, l'eventuale costo per l'attestato di prestazione energetica (APE) e le imposte sulla vendita, che variano a seconda della tua situazione fiscale. Durante la consulenza iniziale, ti forniremo un quadro chiaro di tutti i costi da considerare."
    }
  ];
  
  return (
    <section className="py-24 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="text-secondary uppercase tracking-wider font-semibold">Domande Frequenti</span>
          <h2 className="text-4xl font-monomakh text-primary mt-2 mb-4">Tutto Quello che Devi Sapere</h2>
          <p className="text-gray-600 text-lg">
            Abbiamo raccolto le domande più comuni che ci vengono poste dai proprietari che vogliono vendere casa.
            Se hai ulteriori dubbi, non esitare a contattarci.
          </p>
        </div>
        
        <div className="max-w-3xl mx-auto divide-y divide-gray-200">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="py-6"
            >
              <button
                onClick={() => toggleQuestion(index)}
                className="flex justify-between items-center w-full text-left focus:outline-none"
              >
                <h3 className="text-xl font-semibold text-primary flex items-center">
                  <HelpCircle className="h-5 w-5 mr-2 text-secondary flex-shrink-0" />
                  <span>{faq.question}</span>
                </h3>
                {openIndex === index ? (
                  <ChevronUp className="h-5 w-5 text-primary" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-primary" />
                )}
              </button>
              
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{
                  height: openIndex === index ? 'auto' : 0,
                  opacity: openIndex === index ? 1 : 0
                }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <p className="mt-4 text-gray-600 pl-7">{faq.answer}</p>
              </motion.div>
            </motion.div>
          ))}
        </div>
        
        <div className="max-w-3xl mx-auto mt-12 bg-white p-8 rounded-lg shadow-md">
          <div className="text-center">
            <h3 className="text-xl font-semibold text-primary mb-4">Hai altre domande?</h3>
            <p className="text-gray-600 mb-6">
              Contattaci per una consulenza gratuita e senza impegno. Siamo qui per aiutarti a vendere il tuo immobile
              nelle migliori condizioni possibili.
            </p>
            <a
              href="#valutazione"
              className="inline-block bg-primary hover:bg-primary-light text-white font-semibold px-6 py-3 rounded-lg transition-colors"
            >
              Richiedi una consulenza
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;